import { inspectCandidateImages, type CandidateImageStatus } from './candidateImages';

interface FrameObserverCallbacks {
    onWide: (wide: boolean) => void;
    onError: (message: string, visibleText?: string) => void;
    onReady?: () => void;
    documentId?: string;
    onImageStatus?: (status: CandidateImageStatus) => void;
}

const ROUNDING_GUARD_PX = 1;
const MAX_FRAME_HEIGHT_PX = 100000;
const MAX_DOCUMENT_WIDTH_PX = 100000;
const MAX_CONSECUTIVE_SIZE_CHANGES = 12;
const MAX_SIZE_HISTORY = 16;

// eslint-disable-next-line import/prefer-default-export
export const observeCandidateFrame = (
    iframe: HTMLIFrameElement,
    container: HTMLElement,
    callbacks: FrameObserverCallbacks,
): (() => void) => {
    let stopDocumentObservers = (): void => undefined;
    let disposed = false;
    let pendingFrame = 0;
    let currentDocument: Document | null = null;
    let previousWidth = 0;
    let consecutiveSizeChanges = 0;
    let failed = false;
    const sizeHistory: Array<Record<string, number | boolean>> = [];

    const measure = (): void => {
        pendingFrame = 0;
        const doc = iframe.contentDocument;
        if (disposed || failed || !doc?.body || doc !== currentDocument) return;
        const { body, documentElement: root } = doc;
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) return;
        callbacks.onImageStatus?.(inspectCandidateImages(doc));
        [root, body].forEach((element) => {
            const style = frameWindow.getComputedStyle(element);
            ['margin-top', 'margin-left', 'margin-right', 'margin-bottom'].forEach((property) => {
                if (parseFloat(style.getPropertyValue(property)) < 0) {
                    element.style.setProperty(property, '0', 'important');
                }
            });
        });
        body.querySelectorAll<HTMLElement>('*:not(style)').forEach((element) => {
            const style = frameWindow.getComputedStyle(element);
            if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return;
            const rect = element.getBoundingClientRect();
            [['margin-left', rect.left < 0], ['margin-right', rect.right > root.clientWidth],
                ['margin-top', rect.top < 0]].forEach(([property, outside]) => {
                if (outside && parseFloat(style.getPropertyValue(String(property))) < 0) {
                    element.style.setProperty(String(property), '0', 'important');
                }
            });
            const verticalOverflow = element.scrollHeight > element.clientHeight + ROUNDING_GUARD_PX;
            const horizontalOverflow = element.scrollWidth > element.clientWidth + ROUNDING_GUARD_PX;
            if ((verticalOverflow && /^(auto|scroll|hidden|clip)$/.test(style.overflowY))
                || (horizontalOverflow && /^(hidden|clip)$/.test(style.overflowX))) {
                element.style.setProperty('height', 'auto', 'important');
                element.style.setProperty('max-height', 'none', 'important');
                element.style.setProperty('overflow-y', 'visible', 'important');
                element.style.setProperty('overflow-x', 'visible', 'important');
            }
        });
        const bodyStyle = frameWindow.getComputedStyle(body);
        const rootStyle = frameWindow.getComputedStyle(root);
        const bodyBounds = body.getBoundingClientRect();
        const marginBottom = parseFloat(bodyStyle.marginBottom) || 0;
        const borderBottom = parseFloat(bodyStyle.borderBottomWidth) || 0;
        const rootBottom = (parseFloat(rootStyle.paddingBottom) || 0)
            + (parseFloat(rootStyle.borderBottomWidth) || 0) + (parseFloat(rootStyle.marginBottom) || 0);
        const contentHeight = Math.max(bodyBounds.height, body.scrollHeight + borderBottom);
        const wide = root.scrollWidth > root.clientWidth + ROUNDING_GUARD_PX;
        const scrollbarDifference = iframe.clientHeight - root.clientHeight;
        const horizontalScrollbar = wide && scrollbarDifference > ROUNDING_GUARD_PX ? scrollbarDifference : 0;
        const height = Math.max(1,
            Math.ceil(bodyBounds.top + contentHeight + marginBottom + rootBottom + horizontalScrollbar)
                + ROUNDING_GUARD_PX);
        const nextHeight = `${height}px`;
        sizeHistory.push({
            height,
            clientHeight: iframe.clientHeight,
            rootClientHeight: root.clientHeight,
            contentHeight,
            horizontalScrollbar,
            wide,
        });
        if (sizeHistory.length > MAX_SIZE_HISTORY) sizeHistory.shift();
        iframe.setAttribute('data-candidate-size-history', JSON.stringify(sizeHistory));
        consecutiveSizeChanges = iframe.style.height === nextHeight ? 0 : consecutiveSizeChanges + 1;
        if (!Number.isFinite(height) || height > MAX_FRAME_HEIGHT_PX || root.scrollWidth > MAX_DOCUMENT_WIDTH_PX
            || consecutiveSizeChanges > MAX_CONSECUTIVE_SIZE_CHANGES) {
            failed = true;
            callbacks.onError(
                'This email has an unusually large or unstable layout. Its readable message text is shown below.',
                body.innerText,
            );
            return;
        }
        if (iframe.style.height !== nextHeight) iframe.style.setProperty('height', nextHeight);
        iframe.setAttribute('data-candidate-measured-document', root.getAttribute('data-candidate-document') || '');
        callbacks.onWide(wide);
    };
    const schedule = (): void => {
        if (!disposed && !failed && !pendingFrame) pendingFrame = window.requestAnimationFrame(measure);
    };
    const bindDocument = (): void => {
        stopDocumentObservers();
        const doc = iframe.contentDocument;
        if (!doc?.body) {
            callbacks.onError('The email preview could not be measured. Its complete text is shown below.');
            return;
        }
        if (callbacks.documentId
            && doc.documentElement.getAttribute('data-candidate-document') !== callbacks.documentId) return;
        currentDocument = doc;
        failed = false;
        consecutiveSizeChanges = 0;
        sizeHistory.length = 0;
        iframe.removeAttribute('data-candidate-measured-document');
        iframe.removeAttribute('data-candidate-size-history');
        callbacks.onReady?.();
        const resize = new ResizeObserver(schedule);
        resize.observe(doc.body);
        resize.observe(doc.documentElement);
        const mutations = new MutationObserver(schedule);
        mutations.observe(doc.documentElement, {
            childList: true, subtree: true, attributes: true, characterData: true,
        });
        doc.addEventListener('load', schedule, true);
        doc.addEventListener('error', schedule, true);
        doc.fonts?.addEventListener('loadingdone', schedule);
        doc.fonts?.addEventListener('loadingerror', schedule);
        doc.fonts?.ready.then(schedule);
        iframe.contentWindow?.addEventListener('resize', schedule);
        const navigateFragment = (event: MouseEvent): void => {
            const target = event.target as Element | null;
            const link = target?.closest?.('a[href^="#"]');
            if (!link) return;
            event.preventDefault();
            let id = link.getAttribute('href')?.slice(1) || '';
            try { id = decodeURIComponent(id); } catch { /* Keep a literal malformed fragment. */ }
            const destination = id ? doc.getElementById(id) : doc.body;
            destination?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        };
        doc.addEventListener('click', navigateFragment);
        stopDocumentObservers = (): void => {
            resize.disconnect();
            mutations.disconnect();
            doc.removeEventListener('load', schedule, true);
            doc.removeEventListener('error', schedule, true);
            doc.fonts?.removeEventListener('loadingdone', schedule);
            doc.fonts?.removeEventListener('loadingerror', schedule);
            doc.defaultView?.removeEventListener('resize', schedule);
            doc.removeEventListener('click', navigateFragment);
        };
        schedule();
    };
    const resizeContainer = new ResizeObserver(([entry]) => {
        const width = entry?.contentRect.width ?? 0;
        if (width !== previousWidth) {
            previousWidth = width;
            schedule();
        }
    });
    resizeContainer.observe(container);
    iframe.addEventListener('load', bindDocument);
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    if (iframe.contentDocument?.readyState === 'complete') bindDocument();

    return () => {
        disposed = true;
        window.cancelAnimationFrame(pendingFrame);
        stopDocumentObservers();
        resizeContainer.disconnect();
        iframe.removeEventListener('load', bindDocument);
        window.removeEventListener('resize', schedule);
        window.visualViewport?.removeEventListener('resize', schedule);
    };
};
